import { useCallback, useMemo, type KeyboardEvent, type ReactNode } from 'react';

import {
  AutocompleteResult,
  Icon,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  useDynamicCallback,
  useFavoriteSecurities,
  useMixpanel,
  type RenderResultFunc,
} from '@talos/kyoko';

/** Shared favourite-star prefix for prediction card SearchSelect rows (series / event / outcome). */
export function usePredictionFavoriteSymbol() {
  const mixpanel = useMixpanel();
  const { favoriteSecurities, setFavoriteSecurities } = useFavoriteSecurities();

  const favoriteSecuritiesSet = useMemo(() => new Set(favoriteSecurities), [favoriteSecurities]);

  const toggleFavorite = useDynamicCallback((symbol: string, isFavorite: boolean) => {
    const nextFavorites = isFavorite
      ? favoriteSecurities.filter(favoriteSymbol => favoriteSymbol !== symbol)
      : favoriteSecurities.concat([symbol]);
    setFavoriteSecurities(nextFavorites);
    mixpanel.track(MixpanelEvent.FavoriteSymbol, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.PredictionMarketCard,
    });
  });

  const renderFavoriteIcon = useCallback(
    (symbol: string): ReactNode => {
      const isFavorite = favoriteSecuritiesSet.has(symbol);
      const activate = (e: { stopPropagation: () => void }) => {
        toggleFavorite(symbol, isFavorite);
        e.stopPropagation();
      };
      return (
        <Icon
          icon={isFavorite ? IconName.StarSolid : IconName.Star}
          color={isFavorite ? 'yellow.lighten' : 'gray.080'}
          size="fontSizeLarge"
          role="button"
          tabIndex={0}
          aria-pressed={isFavorite}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          onClick={activate}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activate(e);
            }
          }}
          // Match SymbolSelector: expand hit area, then cancel dropdown row left padding so the star sits near the edge.
          p="spacing12"
          spaceBefore="-spacing12"
          data-testid="favorite-toggle"
        />
      );
    },
    [favoriteSecuritiesSet, toggleFavorite]
  );

  const createFavoriteRenderResult = useCallback(
    <T,>(getSymbol: (item: T) => string | undefined): RenderResultFunc<T> =>
      (searchResult, disabled, searchString) =>
        AutocompleteResult(searchResult, disabled, searchString, item => {
          const symbol = getSymbol(item);
          return symbol ? renderFavoriteIcon(symbol) : null;
        }),
    [renderFavoriteIcon]
  );

  return {
    favoriteSecuritiesSet,
    toggleFavorite,
    renderFavoriteIcon,
    createFavoriteRenderResult,
  };
}
